import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Search, Filter, X, AlertCircle } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Recordings = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [recordings, setRecordings] = useState([]);
    const [categoryPages, setCategoryPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            setError(null);
            try {
                const [recordingsRes, categoriesRes] = await Promise.all([
                    axios.get(`${API}/recordings`),
                    axios.get(`${API}/category-pages`)
                ]);
                setRecordings(recordingsRes.data || []);
                setCategoryPages(categoriesRes.data || []);
            } catch (err) {
                console.error('Failed to fetch data:', err);
                setError(err.message || 'Failed to load recordings. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Legacy ?category= URLs redirect to the new category pages
    useEffect(() => {
        const legacyCategory = searchParams.get('category');
        if (legacyCategory && categoryPages.length > 0) {
            const match = categoryPages.find(c => c.volume === legacyCategory);
            navigate(match ? `/recordings/${match.slug}` : '/recordings', { replace: true });
        }
    }, [searchParams, categoryPages, navigate]);

    const filteredRecordings = recordings.filter(recording => {
        return recording.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
               recording.description.toLowerCase().includes(searchTerm.toLowerCase());
    });

    return (
        <div className="min-h-screen bg-nature-50 pt-24">
            <SEO 
                title="All Recordings"
                description="Explore our complete library of 45 transformational hypnosis sessions. Topics include anxiety, confidence, sleep, weight loss, and more. Download and keep forever."
                canonical="https://hypnosismasteryseries.com/recordings"
            />
            <div className="max-w-7xl mx-auto px-6 py-12">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="font-heading text-4xl sm:text-5xl font-light text-nature-900 mb-4 opacity-0 animate-fade-in-up">
                        All <span className="italic">Recordings</span>
                    </h1>
                    <p className="text-nature-800/70 font-body max-w-2xl mx-auto mb-3 opacity-0 animate-fade-in-up stagger-1">
                        Forty-five guided hypnosis sessions, each about thirty-five minutes, yours to keep forever.
                    </p>
                    <p className="text-nature-800/70 font-body max-w-2xl mx-auto mb-3 opacity-0 animate-fade-in-up stagger-1">
                        Choose by what you want to change — anxiety, sleep, pain, habits, confidence, or the patterns that have not shifted. Nine volumes, four to six sessions in each.
                    </p>
                    <p className="text-nature-800/50 font-body text-sm opacity-0 animate-fade-in-up stagger-1">
                        Best with headphones, somewhere quiet, when you will not be interrupted.
                    </p>
                </div>

                {/* Search */}
                <div className="mb-10 opacity-0 animate-fade-in-up stagger-2">
                    {/* Search Bar */}
                    <div className="relative max-w-md mx-auto mb-10">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-nature-800/40" />
                        <Input
                            type="text"
                            placeholder="Search recordings..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-12 pr-4 py-3 rounded-full border-nature-200 focus:border-copper-500 focus:ring-copper-500/20"
                            data-testid="search-input"
                        />
                    </div>

                    {/* Free session + category cards (hidden while searching) */}
                    {!searchTerm && (
                        <>
                            <Link
                                to="/recording/freedom-from-insomnia"
                                className="group block bg-nature-800 rounded-2xl p-8 sm:p-10 mb-6 transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5"
                                data-testid="free-session-card"
                            >
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                                    <div className="max-w-2xl text-left">
                                        <h2 className="font-heading text-lg font-light text-nature-50 mb-3">Start here — free</h2>
                                        <p className="font-body text-nature-50/75 leading-relaxed">
                                            Freedom from Insomnia is a complete 35-minute session, free to download. Not a sample. Sleep is where most people notice a change first, and it costs nothing to find out whether this approach suits you.
                                        </p>
                                    </div>
                                    <span className="inline-flex items-center self-start sm:self-center whitespace-nowrap rounded-full bg-copper-500 group-hover:bg-copper-600 transition-colors text-white font-body font-medium px-6 py-3">
                                        Listen tonight →
                                    </span>
                                </div>
                            </Link>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="category-cards-grid">
                                {categoryPages.map((cat) => (
                                    <Link
                                        key={cat.slug}
                                        to={`/recordings/${cat.slug}`}
                                        className="group flex flex-col text-left border border-nature-100 rounded-2xl p-6 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                                        style={{
                                            backgroundColor: cat.accent ? `${cat.accent}0d` : '#ffffff',
                                            borderTop: cat.accent ? `3px solid ${cat.accent}` : undefined
                                        }}
                                        data-testid={`category-card-${cat.slug}`}
                                    >
                                        <p className="font-body text-xs font-medium tracking-wide uppercase mb-2" style={{ color: cat.accent || '#b0713f' }}>
                                            {cat.volume}
                                        </p>
                                        <h3 className="font-heading text-lg text-nature-900 mb-2">
                                            {cat.card_title}
                                        </h3>
                                        <p className="text-nature-800/60 font-body text-sm leading-relaxed mb-5">
                                            {cat.card_description}
                                        </p>
                                        <span className="mt-auto text-sm font-body font-medium transition-transform duration-200 group-hover:translate-x-1" style={{ color: cat.accent || '#2d3b33' }}>
                                            5 sessions →
                                        </span>
                                    </Link>
                                ))}
                            </div>

                            <p className="text-center text-nature-800/60 font-body mt-10" data-testid="custom-recording-teaser">
                                Not finding your situation among these? I can also make a session <Link to="/custom-recording" className="text-copper-600 hover:text-copper-700 underline">specially for you</Link>, based on what you tell me.
                            </p>
                        </>
                    )}

                    {/* Active search */}
                    {searchTerm && (
                        <div className="flex items-center justify-center gap-2">
                            <span className="text-sm text-nature-800/60 font-body">
                                {filteredRecordings.length} {filteredRecordings.length === 1 ? 'recording' : 'recordings'} found
                            </span>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSearchTerm('')}
                                className="text-copper-500 hover:text-copper-600 hover:bg-copper-500/10"
                                data-testid="clear-filters"
                            >
                                <X className="w-4 h-4 mr-1" />
                                Clear search
                            </Button>
                        </div>
                    )}
                </div>

                {/* Search results (recordings shown only while searching) */}
                {searchTerm && (
                    loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {[...Array(12)].map((_, i) => (
                                <div key={i} className="bg-nature-100 rounded-2xl aspect-[4/5] skeleton" />
                            ))}
                        </div>
                    ) : error ? (
                        <div className="text-center py-16">
                            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                            <h3 className="font-heading text-xl text-nature-900 mb-2">
                                Unable to load recordings
                            </h3>
                            <p className="text-nature-800/60 font-body mb-4">
                                {error}
                            </p>
                            <Button 
                                onClick={() => window.location.reload()} 
                                variant="outline"
                                className="rounded-full"
                            >
                                Try Again
                            </Button>
                        </div>
                    ) : filteredRecordings.length === 0 ? (
                        <div className="text-center py-16">
                            <Filter className="w-12 h-12 text-nature-800/30 mx-auto mb-4" />
                            <h3 className="font-heading text-xl text-nature-900 mb-2">
                                No recordings found
                            </h3>
                            <p className="text-nature-800/60 font-body">
                                Try adjusting your search
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {filteredRecordings.map((recording, index) => (
                                <ProductCard key={recording.id} recording={recording} index={index} />
                            ))}
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default Recordings;
